/**
 * One tool, edited in a dialog with its sections down the left (2026-09-29).
 *
 * A DIALOG WITH SECTIONS RATHER THAN THE FULL SCREEN IT REPLACES. The screen was one column of
 * everything with a test panel beside it; a tool is really a handful of separate questions -
 * what it does, when, what it needs from the caller, what comes back, what the caller
 * hears - and the nav lets somebody go straight to the one they came to change. Action comes
 * first, as the type does in any "create" dialog: a small switch with the request or the
 * code under it. It also decides whether Response exists, which is for an API request only.
 *
 * ONE SECTION IS MOUNTED AT A TIME, so anything that must survive a trip to another section
 * lives here, not in the section: the draft, the test run's sample details and last result,
 * the fields that result offered for the mapping, and the code line it failed at.
 *
 * THE TECHNICAL SCREEN, ON PURPOSE: this is where somebody who knows what a request is builds
 * one, and every option is shown.
 *
 * SAVE SAVES THIS TOOL, ON ITS OWN, AND THE PAGE'S BAR DOES NOT. Illia's call (2026-09-26):
 * one bar for everything else on Settings, but a tool keeps its own save, because it carries
 * `updatedAt` and a second tab's edit must be refused rather than silently undone. So Save
 * posts, stays open on a refusal with the Worker's sentence, and on a conflict offers to load
 * the other version. Save is off until something changed, so a new tool nobody touched is
 * never created.
 *
 * CANCEL, THE CLOSE BUTTON AND ESCAPE ASK BEFORE THROWING EDITS AWAY, and they ask in the
 * footer rather than in a second dialog over this one. Escape again backs out of the question.
 *
 * THE SWITCH ON THE LIST AND THE SWITCH HERE ARE ONE SETTING. If the list has an unsaved flick
 * for this tool, the editor opens with it, and a successful save settles it.
 */

import { useCallback, useMemo, useRef, useState, type ReactNode } from "react";
import { ApiError, api, post, type AgentPage, type Connection, type Tool } from "../../../api";
import { Alert, Info, List, Phone, Play, Stack, User } from "../../Icons";
import { Picker } from "../../Listbox";
import { SectionDialog } from "../../SectionDialog";
import { Button, Switch, TextArea, TextInput } from "../../../ui";
import { ActionChoice, ResponseEditor } from "./ActionEditor";
import { ParamsEditor } from "./ParamsEditor";
import { TestRunPanel, type TestRun } from "./TestRunPanel";
import { blankTool, fromEditor, sameTool, toEditor, type EditorTool } from "./toolModel";

type Failure = { message: string; conflict: boolean } | null;

type SectionId = "action" | "when" | "details" | "response" | "call" | "test";

/** The nav, in order. `kinds` limits a section to the actions it belongs to. */
const SECTIONS: Array<{
  id: SectionId;
  label: string;
  icon: ReactNode;
  title: string;
  intro: string;
  kinds?: Array<EditorTool["kind"]>;
}> = [
  { id: "action", label: "Action", icon: <Stack size={20} />, title: "Action", intro: "What happens when the AI runs this tool." },
  {
    id: "when",
    label: "When to use",
    icon: <Info size={20} />,
    title: "When to use",
    intro: "The AI reads this on every turn to decide when to run it. Say when not to, and what to confirm with the caller first.",
  },
  {
    id: "details",
    label: "What it needs",
    icon: <User size={20} />,
    title: "What it needs",
    intro: "What the AI has to find out before it runs this. Each description is read by the AI, so say how to ask for it.",
  },
  {
    id: "response",
    label: "Response",
    icon: <List size={20} />,
    title: "Response",
    intro: "What the AI gets from the answer, and what it is told when there is no answer.",
    kinds: ["http"],
  },
  { id: "call", label: "On the call", icon: <Phone size={20} />, title: "On the call", intro: "What the caller hears while it runs, and who hears about it after." },
  { id: "test", label: "Test run", icon: <Play size={20} />, title: "Test run", intro: "Try it with sample details. Nothing reaches a caller." },
];

export function ToolEditor({
  agentId,
  tool,
  connections,
  hoursKnown,
  enabled,
  onClose,
  onSaved,
}: {
  agentId: string;
  /** The tool being edited, or null for a new one. */
  tool: Tool | null;
  connections: Connection[];
  hoursKnown: boolean;
  /** The list's unsaved switch for this tool, when there is one. */
  enabled?: boolean;
  onClose: () => void;
  /** After a save or a delete. The page refetches and settles the list's switch. */
  onSaved: (toolId: string | null) => void;
}) {
  const [original, setOriginal] = useState<EditorTool>(() => (tool ? toEditor(tool) : blankTool()));
  const [draft, setDraft] = useState<EditorTool>(() => ({ ...original, ...(enabled === undefined ? {} : { enabled }) }));
  const [updatedAt, setUpdatedAt] = useState(tool?.updatedAt ?? null);
  const [busy, setBusy] = useState<"save" | "delete" | null>(null);
  const [failure, setFailure] = useState<Failure>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [section, setSection] = useState<SectionId>(tool ? "when" : "action");
  const [testRun, setTestRun] = useState<TestRun>({ args: {}, result: null });
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [errorLine, setErrorLine] = useState<number | null>(null);
  const banner = useRef<HTMLDivElement>(null);

  const dirty = useMemo(() => !sameTool(draft, original), [draft, original]);
  const asking = useRef(false);
  asking.current = confirmDiscard;

  // A second Escape, or the close button, while the footer asks, backs out of the question.
  const requestClose = useCallback(() => {
    if (asking.current) setConfirmDiscard(false);
    else if (dirty) setConfirmDiscard(true);
    else onClose();
  }, [dirty, onClose]);

  const update = (patch: Partial<EditorTool>) => {
    setDraft((current) => ({ ...current, ...patch }));
    setFailure(null);
  };

  function refuse(err: unknown) {
    setFailure({
      message: err instanceof ApiError ? err.message : "The dashboard could not reach the server.",
      conflict: err instanceof ApiError && err.status === 409,
    });
    requestAnimationFrame(() => banner.current?.scrollIntoView?.({ block: "nearest" }));
  }

  async function done() {
    if (!dirty) return onClose();
    setBusy("save");
    setFailure(null);
    try {
      const body = fromEditor(draft);
      if (tool) {
        await post(`/v1/agent/tools/${encodeURIComponent(tool.id)}`, { agent: agentId, tool: body, updatedAt });
        onSaved(tool.id);
      } else {
        const answer = await post<{ tool?: { id: string } }>("/v1/agent/tools", { agent: agentId, tool: body });
        onSaved(answer.tool?.id ?? null);
      }
      onClose();
    } catch (err) {
      refuse(err);
    } finally {
      setBusy(null);
    }
  }

  /** The other tab's version, which replaces this draft. What was typed here is dropped. */
  async function loadTheirs() {
    if (!tool) return;
    try {
      const page = await api<AgentPage>(`/v1/agent?agent=${encodeURIComponent(agentId)}`);
      const theirs = page.tools?.find((t) => t.id === tool.id);
      if (!theirs) {
        setFailure({ message: "This tool has been deleted in the meantime.", conflict: false });
        return;
      }
      const fresh = toEditor(theirs);
      setOriginal(fresh);
      setDraft(fresh);
      setUpdatedAt(theirs.updatedAt);
      setFailure(null);
    } catch (err) {
      refuse(err);
    }
  }

  async function remove() {
    if (!tool) return onClose();
    setBusy("delete");
    try {
      await post(`/v1/agent/tools/${encodeURIComponent(tool.id)}/delete`, { agent: agentId });
      onSaved(null);
      onClose();
    } catch (err) {
      refuse(err);
      setBusy(null);
    }
  }

  function answered(body: string) {
    try {
      const parsed: unknown = JSON.parse(body);
      const root = Array.isArray(parsed) ? parsed[0] : parsed;
      if (root && typeof root === "object") setSuggestions(Object.keys(root as object).slice(0, 12));
    } catch {
      setSuggestions([]);
    }
  }

  const sections = SECTIONS.filter((entry) => !entry.kinds || entry.kinds.includes(draft.kind));
  // Response, when the kind no longer has it, falls back to Action.
  const current = sections.find((entry) => entry.id === section) ?? sections[0]!;

  const footer = confirmDiscard ? (
    <>
      <span className="text-sm font-medium text-ink">Discard your changes to this tool?</span>
      <div className="ml-auto flex gap-2">
        <Button type="button" onClick={() => setConfirmDiscard(false)}>
          Keep editing
        </Button>
        <Button type="button" variant="danger" onClick={onClose}>
          Discard
        </Button>
      </div>
    </>
  ) : (
    <>
      {tool &&
        (confirmDelete ? (
          <>
            <Button type="button" variant="danger" disabled={busy !== null} onClick={() => void remove()}>
              {busy === "delete" ? "Deleting…" : "Delete for good"}
            </Button>
            <Button type="button" variant="ghost" onClick={() => setConfirmDelete(false)}>
              Keep it
            </Button>
          </>
        ) : (
          <button type="button" className="px-1 text-sm font-medium text-ink hover:text-alert" onClick={() => setConfirmDelete(true)}>
            Delete tool
          </button>
        ))}
      <div className="ml-auto flex gap-2">
        <Button type="button" onClick={requestClose}>
          Cancel
        </Button>
        <Button type="button" variant="primary" disabled={!dirty || busy !== null} onClick={() => void done()}>
          {busy === "save" ? "Saving…" : tool ? "Save" : "Create tool"}
        </Button>
      </div>
    </>
  );

  return (
    <SectionDialog
      label={draft.name ? `Tool ${draft.name}` : "New tool"}
      navHead={
        <div className="flex items-center gap-2">
          <input
            aria-label="Tool name"
            className="h-9 min-w-0 flex-1 rounded-sm border border-transparent bg-transparent px-2 font-mono text-sm font-semibold text-ink outline-none placeholder:text-ink-ghost hover:border-line focus:border-line-strong"
            placeholder="look_up_order"
            value={draft.name}
            onChange={(event) => update({ name: event.target.value })}
          />
          <Switch checked={draft.enabled} label="Tool on" onChange={(next) => update({ enabled: next })} />
        </div>
      }
      sections={sections.map(({ id, label, icon }) => ({ id, label, icon }))}
      active={current.id}
      onSelect={setSection}
      title={current.title}
      intro={current.intro}
      banner={
        failure && (
          <div ref={banner} role="alert" className="mb-6 flex items-center gap-3 rounded-md border border-alert-line bg-alert-wash px-4 py-3 text-sm text-ink">
            <Alert size={16} className="flex-none text-alert" />
            <span className="min-w-0 flex-1">
              <b className="font-medium">Not saved.</b> {failure.message}
            </span>
            {failure.conflict && (
              <Button type="button" onClick={() => void loadTheirs()}>
                Load their version
              </Button>
            )}
          </div>
        )
      }
      footer={footer}
      onClose={requestClose}
    >
      {current.id === "action" && <ActionChoice tool={draft} connections={connections} errorLine={errorLine} onChange={update} />}

      {current.id === "when" && (
        <div className="grid gap-6">
          <TextArea
            aria-label="When to use"
            rows={4}
            className="w-full"
            placeholder="e.g. The caller asks whether their repair is finished."
            value={draft.description}
            onChange={(event) => update({ description: event.target.value })}
          />
          <div className="grid max-w-80 gap-1.5">
            <span className="text-sm font-medium text-ink">Availability</span>
            <Picker
              label="Availability"
              value={draft.availability}
              options={[
                { value: "always", label: "Any time" },
                { value: "open", label: "Only during opening hours" },
                { value: "closed", label: "Only outside opening hours" },
              ]}
              onChange={(availability) => update({ availability })}
            />
            {draft.availability !== "always" && !hoursKnown && (
              <span className="text-sm text-warm">Set a timezone and opening hours in Settings, or this is offered all the time.</span>
            )}
          </div>
        </div>
      )}

      {current.id === "details" && (
        <ParamsEditor agentId={agentId} tool={draft} onParams={(params) => update({ params })} onStrict={(strict) => update({ strict })} />
      )}

      {current.id === "response" && <ResponseEditor tool={draft} suggestions={suggestions} onChange={update} />}

      {current.id === "call" && (
        <div className="grid gap-6">
          <div className="grid grid-cols-[minmax(0,1fr)_auto] items-end gap-2.5 max-[720px]:grid-cols-1">
            <label className="grid gap-1.5">
              <span className="text-sm font-medium text-ink">While it waits, the agent says</span>
              <TextInput value={draft.filler} placeholder="One moment, let me check" onChange={(event) => update({ filler: event.target.value })} />
            </label>
            <label className="flex h-9.5 items-center gap-2 text-sm text-ink">
              after
              <TextInput
                type="number"
                min={0}
                max={10}
                step={0.5}
                aria-label="Seconds before it speaks"
                className="w-18 font-mono"
                disabled={!draft.filler.trim()}
                value={draft.fillerAfterMs / 1000}
                onChange={(event) => update({ fillerAfterMs: Math.round(Number(event.target.value) * 1000) })}
              />
              sec
            </label>
          </div>
          <div className="flex flex-wrap items-center gap-2.5 text-sm text-ink">
            If the caller interrupts
            <Picker
              label="If the caller interrupts"
              rootClassName="w-48"
              value={draft.onInterrupt}
              options={[
                { value: "finish", label: "Let it finish" },
                { value: "cancel", label: "Cancel the action" },
              ]}
              onChange={(onInterrupt) => update({ onInterrupt })}
            />
          </div>
          <label className="grid gap-1.5 border-t border-line-soft pt-6">
            <span className="text-sm font-medium text-ink">Email each time it succeeds</span>
            <TextInput type="email" className="max-w-90" placeholder="name@business.com" value={draft.notifyEmail} onChange={(event) => update({ notifyEmail: event.target.value })} />
            <span className="text-sm text-ink">Optional. Sent on real calls only, with what it was given.</span>
          </label>
        </div>
      )}

      {current.id === "test" && (
        <TestRunPanel
          agentId={agentId}
          toolId={tool?.id ?? null}
          tool={draft}
          run={testRun}
          onRun={setTestRun}
          onAnswer={answered}
          onErrorLine={setErrorLine}
        />
      )}
    </SectionDialog>
  );
}
